import { Navigate, useLocation, useParams } from "react-router-dom";
import { Button } from "@volca/ui/components/ui/button";
import { Card } from "@volca/ui/components/ui/card";
import {
  PageContainer,
  PageHeader,
} from "@volca/ui/components/ui/page-components";
import { Skeleton } from "@volca/ui/components/ui/skeleton";
import { Stack } from "@volca/ui/components/ui/stack";
import { useBreadcrumbs } from "@/app-shell";
import { LoadedReport } from "@/profitability/job-costing-run/loaded-report";
import { useReportRun } from "@/profitability/query";
import {
  PROFITABILITY_PATH,
  profitabilityPeriodPath,
  profitabilityTemplatePath,
} from "@/profitability/routes";
import type { ProfitabilityRun } from "@/profitability/types";
import { reportError } from "@/profitability/utils";

/** The run page's tabs the template page has under the same name. */
const SHARED_TABS = new Set(["review", "workbook", "documents"]);

/**
 * Whether the run is a period's result the template page shows: a finished
 * run of a template, with its workbook. A draft's sample (not published),
 * a run still building or one that failed keeps this page.
 */
const onTemplatePage = (run: ProfitabilityRun) =>
  !!run.report &&
  !run.draft_sample &&
  run.status === "completed" &&
  run.workbook_available;

/**
 * A run's own page. A finished period's run opens on its template's page
 * instead, as that period (`?period=`), on the same tab
 * and with any question it carried for the chat.
 */
export const RunDetail = ({
  businessId,
  runId,
}: {
  businessId: string;
  runId: string;
}) => {
  const query = useReportRun(businessId, runId);
  const { pane } = useParams();
  const tab = pane === "sheet" ? "workbook" : pane;
  const location = useLocation();
  useBreadcrumbs([
    { label: "Job Costing", href: PROFITABILITY_PATH },
    ...(query.data?.report
      ? [
          {
            label: query.data.report.name,
            href: profitabilityTemplatePath(query.data.report.id),
          },
        ]
      : []),
    { label: query.data?.display.period ?? "Report" },
  ]);

  if (query.isPending)
    return (
      <PageContainer variant="workspace" aria-busy>
        {/* The header's shape: a title line and the line under it. */}
        <Stack className="w-full" align="start" gap={1}>
          {/* PageTitle's line: 36px at a line-height of 0.95. */}
          <Skeleton className="h-8.5 w-64" />
          <Skeleton className="h-5 w-96 max-w-full" />
        </Stack>
        <Card className="min-h-0 w-full flex-1 gap-0 py-0" />
      </PageContainer>
    );

  // Only when there is nothing to show: a report polls every two seconds while
  // it builds, and one failed poll must not replace it with an error page.
  if (query.error && !query.data)
    return (
      <PageContainer>
        <PageHeader
          title="This report could not be loaded"
          description={reportError(query.error)}
        />
        <Stack className="w-full" gap={0} align="start">
          <Button variant="secondary" onClick={() => void query.refetch()}>
            Try again
          </Button>
        </Stack>
      </PageContainer>
    );

  if (query.data.report && onTemplatePage(query.data))
    return (
      <Navigate
        replace
        to={`${profitabilityPeriodPath(
          query.data.report.id,
          query.data.id,
          tab &&
            (SHARED_TABS.has(tab) ? tab : `pane/${encodeURIComponent(tab)}`),
          location.search,
        )}${location.hash}`}
        state={location.state}
      />
    );

  return (
    <LoadedReport
      key={`${businessId}:${query.data.id}`}
      businessId={businessId}
      run={query.data}
    />
  );
};
